<template>
  <div
    :id="id"
    class="modal"
    tabindex="-1"
    role="dialog"
    aria-labelledby="pricesModalTitle"
    aria-hidden="true"
    data-keyboard="false"
    data-backdrop="static">
    <div
      class="modal-dialog modal-lg-96-percent"
      role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ $t('apps.inventory.label.selectlotnumber') }}
          </h5>
          <button
            type="button"
            class="close"
            aria-label="Close"
            @click="onClose">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <span
            v-if="loading"
          >
            {{ $t('message.loading') }}
          </span>
          <div
            v-else
            style="overflow-x: auto;"
          >
            <base-table
              tableClass="table-border-none"
              hidePagination
            >
              <template #thead>
                <thead>
                  <tr>
                    <th style="width: 40px;"/>
                    <th style="width: 160px;">{{ $t('series.lot') }}</th>
                    <th>{{ $t('apps.inventory.label.product') }}</th>
                    <th>{{ $t('apps.inventory.label.warehouse') }}</th>
                    <th>{{ $t('apps.inventory.label.bin') }}</th>
                    <th style="width: 70px;">
                      <BaseSortLabel
                        v-model="form.modelSort.dateIn"
                        @onChange="handleChangeSortDateIn"
                      >
                        {{ $t('apps.inventory.label.datein') }}
                      </BaseSortLabel>
                    </th>
                    <th style="width: 70px;">
                      <BaseSortLabel
                        v-model="form.modelSort.dateProduct"
                        @onChange="handleChangeSortDateProduct"
                      >
                        {{ $t('apps.inventory.label.dateproduct') }}
                      </BaseSortLabel>
                    </th>
                    <th style="width: 70px;">
                      <BaseSortLabel
                        v-model="form.modelSort.dateExpire"
                        @onChange="handleChangeSortDateExpire"
                      >
                        {{ $t('apps.inventory.label.dateexpire') }}
                      </BaseSortLabel>
                    </th>
                    <th>
                      <BaseSortLabel
                        v-model="form.modelSort.unitBalance"
                        @onChange="handleChangeSortUnitBalance"
                      >
                        {{ $t('apps.inventory.label.unit') }}
                      </BaseSortLabel>
                    </th>
                    <th>
                      <BaseSortLabel
                        v-model="form.modelSort.cost"
                        @onChange="handleChangeSortCost"
                      >
                        {{ $t('apps.inventory.label.cost') }}
                      </BaseSortLabel>
                    </th>
                    <th style="width: 130px;">
                      {{ $t('apps.inventory.label.pick') }}
                      ({{ pickedCount }})
                    </th>
                  </tr>
                </thead>
              </template>
              <template #tbody>
                <tbody>
                  <tr v-if="!form.modelLots.length">
                    <td
                      colspan="11"
                      class="text-center">
                      {{ $t('message.notfounddata') }}
                    </td>
                  </tr>
                  <tr
                    v-for="(item, index) in form.modelLots"
                    :key="index"
                    style="height: 44px;"
                  >
                    <td>
                      <BaseCheckbox
                        v-model="item.checked"
                        :val="true"
                      />
                    </td>
                    <td>
                      <vs-tooltip
                        :text="item.isReady ? $t('apps.inventory.message.lotready') : $t('apps.inventory.message.lotnotready')"
                        position="top"
                        style="float: left;margin: 0px 5px 0 0;"
                      >
                        <i
                          :class="['fas fa-circle', item.isReady ? 'text-success' : 'text-warning']"
                          style="font-size: 9px;position: relative;top: -2px;"/>
                      </vs-tooltip>
                      {{ item.lotCode }}
                    </td>
                    <td>
                      <span class="badge badge-secondary">
                        {{ getProductFullName(item.product.productName, item.sku.attributes) }}
                      </span>
                    </td>
                    <td>
                      {{ item.warehouse.warehouseName }}
                    </td>
                    <td>
                      {{
                        item.warehouse.binLocations.reduce((acc, cur) => {
                          if(cur.id === item.binLocationId) {
                            acc = cur.code
                          }
                          return acc
                        }, null)
                      }}
                    </td>
                    <td>
                      {{ item.dateIn ? getDate(item.dateIn) : null }}
                    </td>
                    <td>
                      {{ item.dateProduct ? getDate(item.dateProduct) : null }}
                    </td>
                    <td>
                      {{ item.dateExpire ? getDate(item.dateExpire) : null }}
                    </td>
                    <td>
                      {{ item.unitBalance }}/{{ item.unitInit }}
                    </td>
                    <td>
                      {{ getNumber(item.cost) }}
                    </td>
                    <td>
                      <BaseInputNumber
                        v-if="item.checked"
                        v-model="item.unit"
                      />
                    </td>
                  </tr>
                </tbody>
              </template>
            </base-table>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-light"
            @click="onCancel">{{ $t('button.cancel') }}</button>
          <button
            :disabled="loading || !form.modelLots.length"
            type="button"
            class="btn btn-web"
            @click="onSubmit">{{ $t('button.apply') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { uuid } from 'vue-uuid'

export default {
  props: ['show', 'loading', 'lots', 'selected', 'index', 'skuId', 'recommendedQty'],

  data () {
    return {
      form: {
        modelLots: [],
        modelSort: {
          dateIn: 'asc',
          dateProduct: null,
          dateExpire: null,
          unitBalance: null,
          cost: null,
        },
      },
    }
  },

  computed: {
    id () {
      return `${uuid.v1()}-select-lot-number-modal`
    },
    pickedCount () {
      const result = this.form.modelLots.reduce((acc, cur) => {
        if (cur.checked) {
          if (cur.unit) {
            acc += Number(cur.unit)
          }
        }
        return acc
      }, 0)
      return this.getNumber(result)
    },
  },

  watch: {
    show () {
      if (this.show) {
        this.clearSort()
        this.setSortToDefault()
        this.setState()
        $(`#${this.id}`).modal('show')

        document.getElementById(this.id).style.zIndex = 1051
        document.querySelectorAll('.modal-backdrop').forEach((ele) => {
          ele.style.zIndex = 1050
        })
      } else {
        $(`#${this.id}`).modal('hide')

        document.getElementById(this.id).style.zIndex = 1050
        document.querySelectorAll('.modal-backdrop').forEach((ele) => {
          ele.style.zIndex = 1040
        })
        const anyModalStillShown = [...document.querySelectorAll('.modal')].some((ele) => ele.style.display === 'block')
        if (anyModalStillShown) {
          window.document.body.classList.add('modal-open')
        }
      }
    },
    index () {
      $(`#${this.id}`).remove()
      this.setModal(`#${this.id}`)
      if (this.show) {
        $(`#${this.id}`).modal('show')
      }
    },
    loading () {
      if (!this.loading) {
        this.setState()
      }
    },
  },

  mounted () {
    this.setModal(`#${this.id}`)
    if (this.show) {
      $(`#${this.id}`).modal('show')
    }
  },

  destroyed () {
    $(`#${this.id}`).remove()
  },

  methods: {
    onClose () {
      this.$emit('onClose')
    },
    onCancel () {
      this.$emit('onCancel')
    },
    onSubmit () {
      const lots = this.form.modelLots.reduce((acc, cur) => {
        if (cur.checked) {
          acc.push({
            skuId: this.skuId,
            lotId: cur.lotId,
            lotCode: cur.lotCode,
            binId: cur.binLocationId,
            binName: cur.warehouse.binLocations.reduce((_acc, _cur) => {
              if (_cur.id === cur.binLocationId) {
                _acc = _cur.code
              }
              return _acc
            }, null),
            unit: cur.unit,
          })
        }
        return acc
      }, [])
      this.$emit('onSubmit', { lots })
    },
    setState () {
      this.form.modelLots = this.lots.map((lot) => {
        const findIndex = this.selected.findIndex((item) => item.lotId === lot.lotId)
        const checked = findIndex >= 0
        const unit = checked ? this.selected[findIndex].unit : lot.unitBalance
        return {
          ...lot,
          checked,
          unit: this.getRecommendedQty(unit),
        }
      })
    },
    clearSort () {
      this.form.modelSort.dateIn = null
      this.form.modelSort.dateProduct = null
      this.form.modelSort.dateExpire = null
      this.form.modelSort.unitBalance = null
      this.form.modelSort.cost = null
    },
    setSortToDefault () {
      this.form.modelSort.dateIn = 'asc'
    },
    handleChangeSortDateIn (sort = null) {
      this.clearSort()
      this.form.modelSort.dateIn = sort
      this.$emit('onSortChange', { key: 'date_in', value: sort })
    },
    handleChangeSortDateProduct (sort = null) {
      this.clearSort()
      this.form.modelSort.dateProduct = sort
      this.$emit('onSortChange', { key: 'date_product', value: sort })
    },
    handleChangeSortDateExpire (sort = null) {
      this.clearSort()
      this.form.modelSort.dateExpire = sort
      this.$emit('onSortChange', { key: 'date_expire', value: sort })
    },
    handleChangeSortUnitBalance (sort = null) {
      this.clearSort()
      this.form.modelSort.unitBalance = sort
      this.$emit('onSortChange', { key: 'unit_balance', value: sort })
    },
    handleChangeSortCost (sort = null) {
      this.clearSort()
      this.form.modelSort.cost = sort
      this.$emit('onSortChange', { key: 'cost', value: sort })
    },
    getRecommendedQty(value) {
      if (this.recommendedQty != null) {
        return this.recommendedQty
      }
      return value
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/base/BaseSelectLotNumberModal.vue